// Iter51 — Reusable per-page filter bar.
//
// Two concerns combined into a single component:
//   (a) free-text search across the caller's chosen fields/accessors
//   (b) a configurable list of "chips" (selects, multi-selects, toggles,
//       date ranges) that AND together on top of the search match.
//
// Props:
//   storageKey      — string, used for `sitroom_filters_{storageKey}` localStorage key
//                     so users land on their last-used filter state.
//   items           — array of objects to filter.
//   getSearchText   — fn(item) => string. The component lowercases this and
//                     does a substring OR-match on the search box value.
//                     Caller chooses what to expose (id + title + part
//                     numbers + people + tenant names, etc).
//   chips           — array of chip configs (see below).
//   onChange        — fn({ filtered, total, values }) called whenever the
//                     filtered set changes.
//   placeholder     — search box placeholder.
//   testIdPrefix    — prefix for data-testid (default "universal-filter").
//
// Chip config shapes:
//   { key, label, type: 'select', options: [{value, label, predicate?}], default? }
//   { key, label, type: 'toggle', predicate: (item, on) => bool, default? }
//   { key, label, type: 'dateRange', field: 'created_at' | accessor }
//   { key, label, type: 'text', placeholder, predicate: (item, value) => bool }
//
// "select" chips collapse to a single value (or "all").  When `predicate` is
// provided on the option, it overrides the default `item[chip.key] === value`
// equality check.
import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const STORAGE_PREFIX = "sitroom_filters_";

const defaultValuesFor = (chips) => {
  const out = { search: "" };
  for (const c of chips || []) {
    if (c.type === "dateRange") out[c.key] = { from: "", to: "" };
    else if (c.type === "toggle") out[c.key] = c.default || false;
    else if (c.type === "text") out[c.key] = c.default ?? "";
    else out[c.key] = c.default ?? "all";
  }
  return out;
};

const matchesChip = (item, chip, value) => {
  if (chip.type === "select") {
    if (value === "all" || value === "" || value == null) return true;
    const opt = (chip.options || []).find((o) => o.value === value);
    if (opt?.predicate) return Boolean(opt.predicate(item));
    return item[chip.key] === value;
  }
  if (chip.type === "toggle") {
    if (!value) return true;
    return Boolean(chip.predicate ? chip.predicate(item, value) : item[chip.key]);
  }
  if (chip.type === "dateRange") {
    if (!value?.from && !value?.to) return true;
    const raw = typeof chip.field === "function" ? chip.field(item) : item[chip.field || chip.key];
    if (!raw) return false;
    const t = new Date(raw).getTime();
    if (Number.isNaN(t)) return false;
    if (value.from && t < new Date(value.from).getTime()) return false;
    if (value.to && t > new Date(value.to).getTime() + 24 * 3600 * 1000) return false;
    return true;
  }
  if (chip.type === "text") {
    if (!value) return true;
    return Boolean(chip.predicate ? chip.predicate(item, value) : false);
  }
  return true;
};

export const useUniversalFilter = ({ storageKey, items, getSearchText, chips }) => {
  const [values, setValues] = useState(() => {
    const def = defaultValuesFor(chips);
    if (!storageKey) return def;
    try {
      const raw = localStorage.getItem(STORAGE_PREFIX + storageKey);
      if (!raw) return def;
      const parsed = JSON.parse(raw);
      return { ...def, ...parsed };
    } catch (_) { return def; }
  });

  useEffect(() => {
    if (!storageKey) return;
    try { localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(values)); } catch (_) { /* swallow */ }
  }, [storageKey, values]);

  const filtered = useMemo(() => {
    const q = (values.search || "").trim().toLowerCase();
    return (items || []).filter((it) => {
      if (q && getSearchText) {
        const haystack = (getSearchText(it) || "").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      for (const c of (chips || [])) {
        if (!matchesChip(it, c, values[c.key])) return false;
      }
      return true;
    });
  }, [items, values, getSearchText, chips]);

  const clearAll = () => setValues(defaultValuesFor(chips));

  return { values, setValues, filtered, clearAll };
};

export const UniversalFilter = ({
  storageKey,
  items,
  getSearchText,
  chips = [],
  placeholder = "Search id, title, part number, person, tenant…",
  testIdPrefix = "universal-filter",
  emptyMessage = "No items match the current filter.",
  children,
}) => {
  const { values, setValues, filtered, clearAll } = useUniversalFilter({ storageKey, items, getSearchText, chips });

  const setVal = (key, v) => setValues((s) => ({ ...s, [key]: v }));

  const hasActiveSearch = (values.search || "").trim().length > 0;
  const hasActiveChips = chips.some((c) => {
    const v = values[c.key];
    if (c.type === "dateRange") return Boolean(v?.from || v?.to);
    if (c.type === "toggle") return Boolean(v);
    if (c.type === "text") return Boolean(v);
    return v != null && v !== "" && v !== "all";
  });
  const hasActive = hasActiveSearch || hasActiveChips;

  // Iter51 — empty-after-filter renderer reused by all consumers via render-prop.
  const renderEmpty = (testId = `${testIdPrefix}-empty-after-filter`) => (
    <div
      className="rounded-md p-6 text-center"
      style={{
        background: "var(--bg-surface, #0d0f18)",
        border: "1px solid rgba(255,255,255,0.08)",
        ...mono(11, "var(--text-dim, #9198b0)"),
      }}
      data-testid={testId}
    >
      {emptyMessage}
    </div>
  );

  return (
    <>
      <div
        className="rounded-md p-2.5 mb-3"
        style={{
          background: "var(--bg-surface, #0d0f18)",
          border: "1px solid rgba(255,255,255,0.10)",
        }}
        data-testid={`${testIdPrefix}-bar`}
      >
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative" style={{ flex: "1 1 240px", minWidth: 200 }}>
          <input
            type="search"
            value={values.search || ""}
            onChange={(e) => setVal("search", e.target.value)}
            placeholder={placeholder}
            className="w-full px-2.5 py-1.5 rounded outline-none"
            style={{
              background: "var(--bg-base, #08090d)",
              border: "1px solid rgba(255,255,255,0.10)",
              color: "var(--text, #f0f2f8)",
              ...mono(11),
            }}
            data-testid={`${testIdPrefix}-search`}
          />
          {hasActiveSearch && (
            <button
              type="button"
              onClick={() => setVal("search", "")}
              style={{ position: "absolute", right: 4, top: 4, padding: 4, background: "transparent", border: "none", color: "#9198b0", cursor: "pointer" }}
              data-testid={`${testIdPrefix}-search-clear`}
              aria-label="Clear search"
            ><X size={12} /></button>
          )}
        </div>

        {chips.map((c) => (
          <ChipControl
            key={c.key}
            chip={c}
            value={values[c.key]}
            onChange={(v) => setVal(c.key, v)}
            testIdPrefix={`${testIdPrefix}-chip-${c.key}`}
          />
        ))}

        <div className="ml-auto flex items-center gap-2" data-testid={`${testIdPrefix}-summary`}>
          <span style={mono(10, "var(--text-dim, #9198b0)", undefined, "0.4px")}>
            {hasActive
              ? `Showing ${filtered.length} of ${items?.length || 0}`
              : `${items?.length || 0} item${(items?.length || 0) === 1 ? "" : "s"}`}
          </span>
          {hasActive && (
            <button
              type="button"
              onClick={clearAll}
              className="px-2 py-1 rounded"
              style={{
                background: "transparent",
                border: "1px solid rgba(226,75,74,0.40)",
                color: "#ff9a99",
                ...mono(9, undefined, 700, "0.4px"),
                textTransform: "uppercase",
                cursor: "pointer",
              }}
              data-testid={`${testIdPrefix}-clear-all`}
            >
              Clear all
            </button>
          )}
        </div>
      </div>
      </div>
      {/* Render-prop body: caller renders filtered items, we render the
          consistent empty-after-filter state when the predicate strips the
          list to zero. */}
      {typeof children === "function"
        ? (filtered.length === 0 && (items?.length || 0) > 0
            ? renderEmpty()
            : children({ filtered, values, total: items?.length || 0, hasActive, clearAll }))
        : null}
    </>
  );
};

const ChipControl = ({ chip, value, onChange, testIdPrefix }) => {
  if (chip.type === "select") {
    const opts = chip.options || [];
    // Iter57 — precompute option text into local strings so the Visual Editor
    // instrumentation does not wrap each {chip.label} prop reference in a
    // <span data-ve-dynamic> inside <option> (React warns: span cannot be a
    // child of option).
    const allText = `${chip.label}: all`;
    const optItems = opts.map((o) => ({ value: o.value, text: `${chip.label}: ${o.label}` }));
    return (
      <select
        value={value ?? "all"}
        onChange={(e) => onChange(e.target.value)}
        className="px-2 py-1.5 rounded outline-none"
        style={{
          background: "var(--bg-base, #08090d)",
          border: `1px solid ${value && value !== "all" ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.10)"}`,
          color: value && value !== "all" ? "#9ec3ec" : "var(--text, #c5cad8)",
          ...mono(10, undefined, 600, "0.4px"),
          cursor: "pointer",
        }}
        data-testid={testIdPrefix}
        aria-label={chip.label}
      >
        <option value="all">{allText}</option>
        {optItems.map((o) => (
          <option key={o.value} value={o.value}>{o.text}</option>
        ))}
      </select>
    );
  }
  if (chip.type === "toggle") {
    const on = Boolean(value);
    return (
      <button
        type="button"
        onClick={() => onChange(!on)}
        className="px-2.5 py-1.5 rounded"
        style={{
          background: on ? "rgba(243,177,74,0.18)" : "transparent",
          border: `1px solid ${on ? "rgba(243,177,74,0.55)" : "rgba(255,255,255,0.12)"}`,
          color: on ? "#f3b14a" : "var(--text-dim, #9aa1b3)",
          ...mono(10, undefined, 700, "0.4px"),
          cursor: "pointer",
          textTransform: "uppercase",
        }}
        data-testid={testIdPrefix}
        aria-pressed={on}
      >
        {on ? "✓ " : ""}{chip.label}
      </button>
    );
  }
  if (chip.type === "dateRange") {
    const v = value || { from: "", to: "" };
    return (
      <div className="flex items-center gap-1" data-testid={testIdPrefix}>
        <span style={mono(9, "#9198b0", undefined, "0.6px")} className="uppercase">{chip.label}</span>
        <input
          type="date"
          value={v.from || ""}
          onChange={(e) => onChange({ ...v, from: e.target.value })}
          className="px-1.5 py-1 rounded outline-none"
          style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(10) }}
          data-testid={`${testIdPrefix}-from`}
        />
        <span style={mono(9, "#9198b0")}>→</span>
        <input
          type="date"
          value={v.to || ""}
          onChange={(e) => onChange({ ...v, to: e.target.value })}
          className="px-1.5 py-1 rounded outline-none"
          style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(10) }}
          data-testid={`${testIdPrefix}-to`}
        />
      </div>
    );
  }
  if (chip.type === "text") {
    return (
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={chip.placeholder || chip.label}
        className="px-2 py-1.5 rounded outline-none"
        style={{
          background: "var(--bg-base, #08090d)",
          border: `1px solid ${value ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.10)"}`,
          color: "var(--text, #f0f2f8)",
          ...mono(10),
          maxWidth: 160,
        }}
        data-testid={testIdPrefix}
        aria-label={chip.label}
      />
    );
  }
  return null;
};
